import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Input,
  Result,
  Select,
  Tag,
  Tooltip,
  message,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { canViewOperationLogs } from '../utils/permissions';
import {
  operationLogApi,
  type OperationLogDTO,
  type OperationLogPage as OperationLogPageData,
} from '../api/kfc';

const { RangePicker } = DatePicker;

const MODULE_OPTIONS = [
  '运单管理',
  '货物管理',
  '客户管理',
  '线路管理',
  '代理管理',
  '供应商管理',
  '产品管理',
  '系统',
];

const ACTION_OPTIONS = [
  { label: '新增', value: 'CREATE' },
  { label: '修改', value: 'UPDATE' },
  { label: '删除', value: 'DELETE' },
  { label: '查看', value: 'VIEW' },
  { label: '导入', value: 'IMPORT' },
  { label: '导出', value: 'EXPORT' },
  { label: '确认重量', value: 'CONFIRM_WEIGHT' },
  { label: '开通KCC账号', value: 'KCC_ACCOUNT_CREATE' },
  { label: '查看KCC密码', value: 'KCC_PASSWORD_VIEW' },
  { label: '重置KCC密码', value: 'KCC_PASSWORD_RESET' },
];

const OperationLogPage: React.FC = () => {
  const { session } = useAuth();
  const allowed = canViewOperationLogs(session.identity?.traits);

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<OperationLogDTO[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [loginName, setLoginName] = useState('');
  const [module, setModule] = useState<string | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [result, setResult] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const load = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true);
      try {
        const params: Record<string, string | number> = { page, perPage: pageSize };
        if (loginName.trim()) params.loginName = loginName.trim();
        if (module) params.module = module;
        if (action) params.action = action;
        if (result) params.result = result;
        if (dateRange?.[0]) params.startTime = dateRange[0].startOf('day').toISOString();
        if (dateRange?.[1]) params.endTime = dateRange[1].endOf('day').toISOString();

        const res = await operationLogApi.list(params);
        const data: OperationLogPageData = res.data;
        setLogs(data.data || []);
        setPagination({
          current: data.page || page,
          pageSize: data.per_page || pageSize,
          total: data.total || 0,
        });
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string }; status?: number } };
        if (err.response?.status === 403) {
          message.error('无权查看操作日志');
        } else {
          message.error(err.response?.data?.message || '加载操作日志失败');
        }
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [loginName, module, action, result, dateRange]
  );

  useEffect(() => {
    if (allowed) {
      load(1, pagination.pageSize);
    }
  }, [allowed]); // initial load when access is granted; search uses explicit button

  if (!allowed) {
    return (
      <Result
        status="403"
        title="无权查看操作日志"
        subTitle="仅管理员（admin / super_admin）可访问此页面。"
      />
    );
  }

  const columns: ColumnsType<OperationLogDTO> = [
    {
      title: '操作时间',
      dataIndex: 'operatedAt',
      width: 180,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '登录名称',
      dataIndex: 'loginName',
      width: 200,
      ellipsis: true,
      render: (v: string, r) => v || r.displayName || '-',
    },
    {
      title: '模块',
      dataIndex: 'module',
      width: 110,
    },
    {
      title: '操作对象',
      key: 'resource',
      width: 180,
      ellipsis: true,
      render: (_, r) => {
        if (r.resourceType && r.resourceId) return `${r.resourceType}=${r.resourceId}`;
        if (r.resourceType) return r.resourceType;
        return '-';
      },
    },
    {
      title: '操作名称',
      dataIndex: 'actionLabel',
      width: 90,
      render: (v: string, r) => v || r.action || '-',
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 90,
      render: (v: string) =>
        v === 'SUCCESS' ? <Tag color="success">成功</Tag> : <Tag color="error">失败</Tag>,
    },
    {
      title: '摘要',
      dataIndex: 'summary',
      ellipsis: true,
      render: (v: string, r) => (
        <Tooltip title={r.errorMessage ? `${v || ''}\n${r.errorMessage}` : v}>
          <span>{v || '-'}</span>
        </Tooltip>
      ),
    },
  ];

  const onTableChange = (p: TablePaginationConfig) => {
    load(p.current || 1, p.pageSize || 20);
  };

  return (
    <Card
      title="操作日志"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => load(pagination.current, pagination.pageSize)}>
          刷新
        </Button>
      }
    >
      <FilterDrawer variant="space" style={{ marginBottom: 16 }}>
        <Input
          placeholder="登录名称"
          allowClear
          value={loginName}
          onChange={(e) => setLoginName(e.target.value)}
          style={{ width: 200 }}
        />
        <Select
          placeholder="模块"
          allowClear
          value={module}
          onChange={setModule}
          style={{ width: 140 }}
          options={MODULE_OPTIONS.map((m) => ({ label: m, value: m }))}
        />
        <Select
          placeholder="操作名称"
          allowClear
          value={action}
          onChange={setAction}
          style={{ width: 120 }}
          options={ACTION_OPTIONS}
        />
        <Select
          placeholder="结果"
          allowClear
          value={result}
          onChange={setResult}
          style={{ width: 110 }}
          options={[
            { label: '成功', value: 'SUCCESS' },
            { label: '失败', value: 'FAILURE' },
          ]}
        />
        <RangePicker value={dateRange} onChange={(v) => setDateRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => load(1, pagination.pageSize)}
        >
          查询
        </Button>
      </FilterDrawer>
      <AdaptiveTable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={logs}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
        }}
        onChange={onTableChange}
        expandable={{
          expandedRowRender: (r) => (
            <div style={{ fontSize: 12, color: '#666' }}>
              <div>请求：{r.requestMethod} {r.requestPath}</div>
              {r.clientIp && <div>IP：{r.clientIp}</div>}
              {r.durationMs != null && <div>耗时：{r.durationMs} ms</div>}
              {r.errorMessage && <div style={{ color: '#cf1322' }}>错误：{r.errorMessage}</div>}
              {r.details && Object.keys(r.details).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div>详情：</div>
                  <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(r.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ),
        }}
      />
    </Card>
  );
};

export default OperationLogPage;
